import { Col, Row } from 'components/base';
import { Visible } from 'design-system/base';
import { FONT_FAMILY } from 'design-system/theme';
import React, { useCallback, useState } from 'react';
import { Provider as PaperProvider } from 'react-native-paper';

import { Wrapper } from '../internal';
import { formatNumberToMl, outputReshapers } from './formatters';
import {
  StyledDatePickerInput,
  StyledTimePickerInput,
  TextWrapper,
  StyledText,
  useCustomStyles,
} from './MaskedTextBox.styles';
import { MaskType } from './types';

type ValidRange = { startDate?: Date; endDate?: Date; disabledDates?: Date[] };

type TimeProps = { hours: number; minutes: number };
export interface DateProps {
  /**
   * The Mask type for dates will use the same ISO format FHI uses as the input and output value
   */
  maskType: MaskType;
  label?: string;
  value?: string;
  errors?: string;
  isDisabled?: boolean;
  onChange: (value?: string) => void;
  validRange?: ValidRange;
}

const getInitialDateFromValue = (date?: string): Date | undefined => {
  if (!date) return undefined;
  if (date.length === 0) return undefined;

  return new Date(date);
};

/**
 * Note that input will return the datetime in the requested format as a string and not a `Date`.
 *
 * Based on this component's structure it should be trivial to add additional types by adding to the
 * `MaskType` list and making some minor adjustments to the data passed to the `Input`
 *
 * This component also exports it's parsers so that the resulting values can be used outside of the
 * component
 */
export const DateComponent: React.FC<DateProps> = ({
  label = '',
  value = '',
  errors,
  isDisabled,
  onChange,
  maskType,
  validRange,
}) => {
  const { paperTheme, theme } = useCustomStyles();
  const [inputDate, setInputDate] = useState<Date | undefined>(getInitialDateFromValue(value));
  const [visible, setVisible] = useState<boolean>(false);
  const [reshapedDate, setReshapedDate] = useState<string>();
  const [time, setTime] = useState<string>();

  const outputReshaper = outputReshapers[maskType];

  const setTimeInput = useCallback(
    (time?: TimeProps, reshapedDate?: string) => {
      const currentDate = new Date();
      const date = reshapedDate ? new Date(reshapedDate) : currentDate;
      const getHours = time ? time.hours : currentDate.getHours();
      const getMinutes = time ? time.minutes : currentDate.getMinutes();
      const initialTime = date.getTime() + formatNumberToMl('minutes', date.getTimezoneOffset());

      const hoursMlSec = formatNumberToMl('hours', getHours);
      const minutesMlSec = formatNumberToMl('minutes', getMinutes);

      const dateTime = new Date(initialTime + hoursMlSec + minutesMlSec);
      const reshaped = outputReshaper(dateTime);

      setVisible(false);

      setTime(`${String(getHours).padStart(2, '0')}:${String(getMinutes).padStart(2, '0')}`);

      onChange(reshaped);
    },
    [onChange, outputReshaper]
  );

  const onDismiss = useCallback(() => {
    setTimeInput(undefined, reshapedDate);
    setVisible(false);
  }, [reshapedDate, setVisible, setTimeInput]);

  const handleChange = useCallback(
    (date?: Date) => {
      setInputDate(date);
      date?.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      const parsed = date;

      if (parsed) {
        const reshaped = outputReshaper(date);

        setReshapedDate(reshaped);

        setVisible(true);

        onChange(reshaped);
      } else {
        onChange(undefined);
      }
    },
    [onChange, outputReshaper]
  );

  const onConfirm = useCallback(
    (time: TimeProps) => {
      if (reshapedDate) {
        setTimeInput(time, reshapedDate);
      } else {
        onChange(undefined);
      }
    },
    [reshapedDate, onChange, setTimeInput]
  );

  const hours = new Date()?.getHours();
  const minutes = new Date()?.getMinutes();
  const isDateComponent = maskType === 'date';

  return (
    <Wrapper label={label} error={errors}>
      <Visible if={isDateComponent}>
        <Col flex={1}>
          <Row>
            <PaperProvider theme={paperTheme}>
              <StyledDatePickerInput
                locale="en-GB"
                value={inputDate}
                onChange={(d?: Date) => handleChange(d)}
                inputMode="start"
                validRange={validRange}
                saveLabel={'Save'}
                uppercase={false}
                disabled={isDisabled}
              />
            </PaperProvider>
          </Row>
        </Col>
      </Visible>

      <Visible if={!isDateComponent}>
        <Col flex={0.7}>
          <Row>
            <PaperProvider theme={paperTheme}>
              <StyledDatePickerInput
                locale="en-GB"
                value={inputDate}
                onChange={(d?: Date) => handleChange(d)}
                inputMode="start"
                validRange={validRange}
                saveLabel={'Save'}
                uppercase={false}
                disabled={isDisabled}
                style={{ fontFamily: FONT_FAMILY.regular }}
              />
              <StyledTimePickerInput
                visible={visible}
                onDismiss={onDismiss}
                onConfirm={onConfirm}
                hours={hours}
                minutes={minutes}
                defaultInputType={'keyboard'}
              />
            </PaperProvider>
          </Row>
        </Col>
        <Col flex={0.3}>
          <TextWrapper theme={theme}>
            <Visible if={time}>
              <StyledText color="text-default">{time}</StyledText>
            </Visible>
          </TextWrapper>
        </Col>
      </Visible>
    </Wrapper>
  );
};
